import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Reveal from '@/components/Reveal';
import Img from '@/components/Img';
import Seo from '@/components/Seo';
import Faq from '@/components/Faq';
import { WOMENS_PAGE } from '@/constants/testIds';
import { WOMENS_CATEGORIES } from '@/constants/site';
import { WOMENS_FAQS } from '@/constants/faqs';

const HERO_IMG = '/assets/w-bridal.jpg';

export default function WomensDesign() {
  return (
    <div data-testid={WOMENS_PAGE.root} className="bg-[hsl(var(--bb-ivory))]">
      <Seo
        title="Women's Design — VM by Vandana | Bridal Lehengas, Gowns, Saris | Dubai"
        description="VM by Vandana — bespoke lehengas, gowns, saris, resort wear and custom couture in Al Karama, Dubai. Bridal commissions, evening wear and made-to-measure for women, by designer Mrs. Vandana."
        canonical="https://bombaybespokefashion.com/womens-design"
      />
      {/* HERO */}
      <section className="relative bg-[hsl(var(--bb-ivory))] overflow-hidden" data-testid={WOMENS_PAGE.hero}>
        <div className="bb-container pt-14 md:pt-24 pb-12 md:pb-20 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-6">
            <Reveal>
              <div className="bb-eyebrow-emerald">VM by Vandana · Women’s Design</div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="bb-display text-[58px] sm:text-[80px] lg:text-[104px] mt-5 text-[hsl(var(--bb-text))] leading-[0.92]">
                A house of
                <br />
                <span className="bb-italic text-[hsl(var(--bb-emerald))]">Indian couture</span>
                <br />
                heritage.
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-7 max-w-xl text-base md:text-lg text-[hsl(var(--bb-text-muted))] leading-relaxed">
                Made-to-order silhouettes for the modern bride, the evening guest, and the
                everyday woman who values craft. Every garment by Mrs. Vandana begins as
                a sketch and ends in the hand of the woman who wears it.
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <Reveal className="bb-image-hover relative aspect-[4/5] overflow-hidden">
              <Img src={HERO_IMG} alt="VM by Vandana lehenga" imgClassName="w-full h-full object-cover" />
              <span className="absolute left-4 bottom-4 px-3 py-2 bg-[hsl(var(--bb-text))]/90 text-[10px] tracking-[0.24em] uppercase text-[hsl(var(--bb-emerald))]">
                Bridal · Made to order
              </span>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="bb-container pb-24 md:pb-32">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <Reveal>
            <h2 className="bb-display text-4xl md:text-5xl text-[hsl(var(--bb-text))]">The Collections</h2>
          </Reveal>
          <Reveal delay={120}>
            <span className="bb-eyebrow-emerald">Six houses of design</span>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-5 md:gap-7">
          {WOMENS_CATEGORIES.map((cat, i) => {
            // 3 + 3 grid (six categories in two rows)
            const span = 'col-span-12 sm:col-span-6 md:col-span-4';
            const aspect = 'aspect-[4/5]';
            return (
              <Reveal key={cat.slug} delay={i * 80} className={span}>
                <div
                  data-testid={WOMENS_PAGE.categoryCard(cat.slug)}
                  className="block relative overflow-hidden bg-[hsl(var(--bb-ivory-soft))]"
                >
                  <div className={`${aspect} relative overflow-hidden bb-image-hover`}>
                    <Img src={cat.image} alt={cat.title} imgClassName="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    {!cat.available && (
                      <span className="absolute top-4 right-4 px-3 py-1.5 bg-[hsl(var(--bb-text))]/95 text-[hsl(var(--bb-emerald))] text-[10px] tracking-[0.24em] uppercase">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <div className="absolute left-0 right-0 bottom-0 p-6 md:p-7 text-[hsl(var(--bb-text))]">
                    <div className="bb-display text-3xl md:text-4xl">{cat.title}</div>
                    <div className="mt-2 text-sm text-[hsl(var(--bb-text))]/80 max-w-md">{cat.blurb}</div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={200} className="mt-16 text-center">
          <div className="bb-display text-3xl md:text-4xl text-[hsl(var(--bb-text))]">
            Design <span className="bb-italic text-[hsl(var(--bb-emerald))]">your dream</span> piece.
          </div>
          <Link
            to="/book-appointment"
            data-testid={WOMENS_PAGE.bookCta}
            className="mt-8 inline-flex items-center gap-3 px-7 py-4 bg-[hsl(var(--bb-emerald))] text-[hsl(var(--bb-text))] uppercase tracking-[0.24em] text-[11px] font-medium hover:bg-[hsl(var(--bb-emerald-deep))] transition-colors duration-500"
          >
            Book a consultation
            <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>

      <section className="bg-[hsl(var(--bb-ivory-soft))] border-t border-[hsl(var(--bb-text))]/8">
        <Faq
          eyebrow="VM by Vandana · FAQs"
          heading="Bespoke couture,"
          italicWord="explained."
          items={WOMENS_FAQS}
          tone="emerald"
        />
      </section>
    </div>
  );
}